import { UserData, PensionResult } from '../contexts/UserDataContext';

// Constants for calculations based on ZUS regulations
const MINIMUM_PENSION = 1588.44; // Minimalna emerytura 2024
const AVERAGE_WAGE_GROWTH_RATE = 0.03; // 3% annual growth (based on economic forecasts)
const INFLATION_RATE = 0.025; // 2.5% annual inflation (average forecast)
const CONTRIBUTION_RATE = 0.1952; // 19.52% of gross salary (składka emerytalna)
const SUBACCOUNT_RATE = 0.1222; // 12.22% goes to subaccount (OFE/subkonto)
const MAIN_ACCOUNT_RATE = 0.073; // 7.3% goes to main account
const SICK_LEAVE_REDUCTION = 0.04; // 4% reduction for sick leave
const TAX_FREE_AMOUNT = 30000; // Kwota wolna od podatku 2024
const HEALTH_INSURANCE_RATE = 0.09; // 9% składka zdrowotna

// Average pension data by year (based on Ministry of Finance forecasts)
const AVERAGE_PENSIONS_BY_YEAR: Record<number, number> = {
  2024: 2800,
  2025: 2884,
  2026: 2970,
  2027: 3059,
  2028: 3151,
  2029: 3245,
  2030: 3342,
  2035: 3875,
  2040: 4490,
  2045: 5205,
  2050: 6030,
  2055: 6990,
  2060: 8100,
  2065: 9390,
  2070: 10890,
  2075: 12620,
  2080: 14630
};

// Life expectancy tables (średnie dalsze trwanie życia) - GUS data
const LIFE_EXPECTANCY: Record<string, Record<number, number>> = {
  male: {
    60: 21.5, 61: 20.7, 62: 19.8, 63: 19.0, 64: 18.2, 65: 17.4, 66: 16.6, 67: 15.8, 68: 15.1, 69: 14.3, 70: 13.6
  },
  female: {
    60: 26.0, 61: 25.1, 62: 24.2, 63: 23.3, 64: 22.5, 65: 21.6, 66: 20.8, 67: 20.0, 68: 19.2, 69: 18.4, 70: 17.6
  }
};

// GDP growth forecasts by year (prognoza PKB)
const GDP_GROWTH_BY_YEAR: Record<number, number> = {
  2024: 0.031, 2025: 0.032, 2026: 0.033, 2027: 0.032, 2028: 0.031,
  2029: 0.030, 2030: 0.030, 2035: 0.028, 2040: 0.026, 2045: 0.024,
  2050: 0.022, 2055: 0.020, 2060: 0.020, 2065: 0.019, 2070: 0.018,
  2075: 0.017, 2080: 0.016
};

// Inflation forecasts by year (prognoza inflacji)
const INFLATION_BY_YEAR: Record<number, number> = {
  2024: 0.038, 2025: 0.032, 2026: 0.027, 2027: 0.025, 2028: 0.025,
  2029: 0.025, 2030: 0.025, 2035: 0.025, 2040: 0.025, 2045: 0.025,
  2050: 0.025, 2055: 0.025, 2060: 0.025, 2065: 0.025, 2070: 0.025,
  2075: 0.025, 2080: 0.025
};

export const calculatePension = (userData: UserData): PensionResult => {
  const {
    gender,
    grossSalary,
    workStartYear,
    workEndYear,
    accumulatedFunds,
    includeSickLeave,
      age
  } = userData;

  const workYears = workEndYear - workStartYear;
  const retirementAge = age + workYears;
  
  // Calculate indexed salary progression with year-specific GDP growth
  const salaryProgression = calculateSalaryProgression(
    grossSalary,
    workStartYear,
    workEndYear
  );

  // Calculate total contributions split between accounts
  const contributionBreakdown = calculateTotalContributions(
    salaryProgression,
    workStartYear,
    workEndYear,
    includeSickLeave
  );

  // Calculate accumulated funds with valorization
  const finalAccumulatedFunds = accumulatedFunds || contributionBreakdown.total;
  const valorizedCapital = calculateValorization(
    finalAccumulatedFunds,
    workStartYear,
    workEndYear
  );

  // Get life expectancy for gender and retirement age
  const lifeExpectancy = getLifeExpectancy(gender, retirementAge);
  const monthlyDivisor = lifeExpectancy * 12;

  // Calculate base pension using the formula: Capital / (Life Expectancy in months)
  const basePension = valorizedCapital / monthlyDivisor;

  // Apply minimum pension guarantee
  const actualAmount = Math.max(basePension, MINIMUM_PENSION);

  // Calculate real (inflation-adjusted) amount
  const yearsToRetirement = workEndYear - new Date().getFullYear();
  const averageInflationRate = calculateAverageInflation(new Date().getFullYear(), workEndYear);
  const realAmount = actualAmount / Math.pow(1 + averageInflationRate, yearsToRetirement);

  // Get average pension in retirement year
  const averagePensionInRetirementYear = getAveragePensionForYear(workEndYear);

  // Calculate replacement rate
  const finalSalary = salaryProgression[salaryProgression.length - 1];
  const replacementRate = (actualAmount / finalSalary) * 100;

  // Calculate salary with and without sick leave
  const salaryWithoutSickLeave = finalSalary;
  const salaryWithSickLeave = includeSickLeave 
    ? finalSalary * (1 - SICK_LEAVE_REDUCTION)
    : finalSalary;

  // Calculate additional years scenarios
  const additionalYears = calculateAdditionalYears(
    actualAmount,
    valorizedCapital,
    workYears,
    gender,
    workEndYear
  );

  // Calculate average GDP growth
  const averageGDPGrowth = calculateAverageGDP(workStartYear, workEndYear);

  return {
    actualAmount: Math.round(actualAmount * 100) / 100,
    realAmount: Math.round(realAmount * 100) / 100,
    averagePensionInRetirementYear: Math.round(averagePensionInRetirementYear * 100) / 100,
    replacementRate: Math.round(replacementRate * 100) / 100,
    salaryWithoutSickLeave: Math.round(salaryWithoutSickLeave * 100) / 100,
    salaryWithSickLeave: Math.round(salaryWithSickLeave * 100) / 100,
    additionalYears,
    calculationDetails: {
      totalContributions: Math.round(contributionBreakdown.total * 100) / 100,
      mainAccountContributions: Math.round(contributionBreakdown.mainAccount * 100) / 100,
      subAccountContributions: Math.round(contributionBreakdown.subAccount * 100) / 100,
      accumulatedCapital: Math.round(finalAccumulatedFunds * 100) / 100,
      valorizedCapital: Math.round(valorizedCapital * 100) / 100,
      lifeExpectancy: Math.round(lifeExpectancy * 10) / 10,
      monthlyDivisor: Math.round(monthlyDivisor * 100) / 100,
      workYears,
      retirementAge,
      averageGDPGrowth: Math.round(averageGDPGrowth * 1000) / 10, // as percentage with 1 decimal
      averageInflation: Math.round(averageInflationRate * 1000) / 10, // as percentage with 1 decimal
      contributionRate: Math.round(CONTRIBUTION_RATE * 10000) / 100, // as percentage
      taxFreeAmount: TAX_FREE_AMOUNT,
      healthInsuranceRate: Math.round(HEALTH_INSURANCE_RATE * 10000) / 100 // as percentage
    }
  };
};

const calculateSalaryProgression = (
  initialSalary: number,
  startYear: number,
  endYear: number
): number[] => {
  const progression: number[] = [];
  let currentSalary = initialSalary;

  for (let year = startYear; year <= endYear; year++) {
    progression.push(currentSalary);
    const gdpGrowth = getGDPGrowthForYear(year);
    currentSalary *= (1 + gdpGrowth);
  }

  return progression;
};

const calculateTotalContributions = (
  salaryProgression: number[],
  startYear: number,
  endYear: number,
  includeSickLeave: boolean
): { total: number; mainAccount: number; subAccount: number } => {
  let totalContributions = 0;
  let mainAccountContributions = 0;
  let subAccountContributions = 0;
  const sickLeaveReduction = includeSickLeave ? SICK_LEAVE_REDUCTION : 0;

  salaryProgression.forEach((salary, index) => {
    const adjustedSalary = salary * (1 - sickLeaveReduction);
    const annualContribution = adjustedSalary * CONTRIBUTION_RATE;
    const mainAccountPart = adjustedSalary * MAIN_ACCOUNT_RATE;
    const subAccountPart = adjustedSalary * SUBACCOUNT_RATE;

    totalContributions += annualContribution;
    mainAccountContributions += mainAccountPart;
    subAccountContributions += subAccountPart;
  });

  return {
    total: totalContributions,
    mainAccount: mainAccountContributions,
    subAccount: subAccountContributions
  };
};

const calculateValorization = (
  capital: number,
  startYear: number,
  endYear: number
): number => {
  let valorizedCapital = capital;

  for (let year = startYear; year <= endYear; year++) {
    const gdpGrowth = getGDPGrowthForYear(year);
    const inflation = getInflationForYear(year);
    // Waloryzacja = wzrost PKB + inflacja
    const valorizationRate = gdpGrowth + inflation;
    valorizedCapital *= (1 + valorizationRate);
  }

  return valorizedCapital;
};

const getLifeExpectancy = (gender: 'male' | 'female', retirementAge: number): number => {
  const table = LIFE_EXPECTANCY[gender];
  const age = Math.min(Math.max(Math.round(retirementAge), 60), 70);
  return table[age] || (gender === 'male' ? 17.4 : 21.6);
};

const getGDPGrowthForYear = (year: number): number => {
  const years = Object.keys(GDP_GROWTH_BY_YEAR).map(Number).sort((a, b) => a - b);

  if (year <= years[0]) return GDP_GROWTH_BY_YEAR[years[0]];
  if (year >= years[years.length - 1]) return GDP_GROWTH_BY_YEAR[years[years.length - 1]];

  // Linear interpolation
  for (let i = 0; i < years.length - 1; i++) {
    if (year >= years[i] && year <= years[i + 1]) {
      const ratio = (year - years[i]) / (years[i + 1] - years[i]);
      return GDP_GROWTH_BY_YEAR[years[i]] +
        ratio * (GDP_GROWTH_BY_YEAR[years[i + 1]] - GDP_GROWTH_BY_YEAR[years[i]]);
    }
  }

  return AVERAGE_WAGE_GROWTH_RATE;
};

const getInflationForYear = (year: number): number => {
  const years = Object.keys(INFLATION_BY_YEAR).map(Number).sort((a, b) => a - b);

  if (year <= years[0]) return INFLATION_BY_YEAR[years[0]];
  if (year >= years[years.length - 1]) return INFLATION_BY_YEAR[years[years.length - 1]];

  // Linear interpolation
  for (let i = 0; i < years.length - 1; i++) {
    if (year >= years[i] && year <= years[i + 1]) {
      const ratio = (year - years[i]) / (years[i + 1] - years[i]);
      return INFLATION_BY_YEAR[years[i]] +
        ratio * (INFLATION_BY_YEAR[years[i + 1]] - INFLATION_BY_YEAR[years[i]]);
    }
  }

  return INFLATION_RATE;
};

const calculateAverageGDP = (startYear: number, endYear: number): number => {
  let sum = 0;
  let count = 0;

  for (let year = startYear; year <= endYear; year++) {
    sum += getGDPGrowthForYear(year);
    count++;
  }

  return count > 0 ? sum / count : AVERAGE_WAGE_GROWTH_RATE;
};

const calculateAverageInflation = (startYear: number, endYear: number): number => {
  let sum = 0;
  let count = 0;
  
  for (let year = startYear; year <= endYear; year++) {
    sum += getInflationForYear(year);
    count++;
  }

  return count > 0 ? sum / count : INFLATION_RATE;
};

const getAveragePensionForYear = (year: number): number => {
  // Find the closest year in our data
  const years = Object.keys(AVERAGE_PENSIONS_BY_YEAR).map(Number).sort((a, b) => a - b);
  
  if (year <= years[0]) return AVERAGE_PENSIONS_BY_YEAR[years[0]];
  if (year >= years[years.length - 1]) return AVERAGE_PENSIONS_BY_YEAR[years[years.length - 1]];
  
  // Linear interpolation
  for (let i = 0; i < years.length - 1; i++) {
    if (year >= years[i] && year <= years[i + 1]) {
      const ratio = (year - years[i]) / (years[i + 1] - years[i]);
      return AVERAGE_PENSIONS_BY_YEAR[years[i]] + 
        ratio * (AVERAGE_PENSIONS_BY_YEAR[years[i + 1]] - AVERAGE_PENSIONS_BY_YEAR[years[i]]);
    }
  }
  
  return AVERAGE_PENSIONS_BY_YEAR[2024];
};

const calculateAdditionalYears = (
  currentPension: number,
  valorizedCapital: number,
  workYears: number,
  gender: 'male' | 'female',
  currentRetirementYear: number
) => {
  const calculatePensionForYears = (additionalYears: number) => {
    const newWorkYears = workYears + additionalYears;
    const retirementAge = 60 + newWorkYears; // Simplified

    // Add contributions for additional years (simplified)
    const additionalCapital = valorizedCapital * (1 + additionalYears * 0.15);

    // Recalculate with new life expectancy
    const lifeExpectancy = getLifeExpectancy(gender, retirementAge);
    const monthlyDivisor = lifeExpectancy * 12;
    const newPension = additionalCapital / monthlyDivisor;

    return Math.max(newPension, MINIMUM_PENSION);
  };

  return {
    oneYear: Math.round(calculatePensionForYears(1) * 100) / 100,
    twoYears: Math.round(calculatePensionForYears(2) * 100) / 100,
    fiveYears: Math.round(calculatePensionForYears(5) * 100) / 100
  };
};

export const calculateYearsToExpectedPension = (
  expectedPension: number,
  userData: UserData
): number | null => {
  const currentResult = calculatePension(userData);
  
  if (currentResult.actualAmount >= expectedPension) {
    return 0;
  }

  // Binary search to find the number of additional years needed
  let low = 0;
  let high = 20; // Maximum 20 additional years
  let result = null;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const testData = {
      ...userData,
      workEndYear: userData.workEndYear + mid
    };
    const testResult = calculatePension(testData);
    
    if (testResult.actualAmount >= expectedPension) {
      result = mid;
      high = mid - 1;
    } else {
      low = mid + 1;
    }
  }

  return result;
};
